import { PayloadAction } from '@reduxjs/toolkit'
import { selectCredentials } from '@views/loginView/state/loginSlice'
import { call, put, select, takeEvery, takeLeading } from 'redux-saga/effects'
import {
  createCommissionSettlement,
  deleteCommissionSettlement,
  getCommissionExecutives,
  getCommissionPreview,
  getCommissionSettlements
} from '@http/executiveCommissionsService'
import {
  CommissionPreview,
  CommissionSettlement,
  CreateSettlementPayload,
  DeleteSettlementPayload,
  DeleteSettlementResult,
  ExecutiveOption
} from '../types'
import {
  commissionDataFailed,
  commissionDataLoaded,
  createSettlement,
  deleteSettlement,
  executivesLoaded,
  loadCommissionData,
  loadExecutives,
  settlementCreated,
  settlementDeleted
} from './executiveCommissionsSlice'

function errorMessage(error: Error): string {
  return (error as any)?.response?.data?.message || error.message || 'No fue posible consultar las comisiones.'
}

function* deleteSettlementHttp(action: PayloadAction<DeleteSettlementPayload>) {
  const { cocap } = yield select(selectCredentials)
  try {
    const result: DeleteSettlementResult = yield call(
      deleteCommissionSettlement,
      cocap,
      action.payload.settlementId,
      action.payload.reason
    )
    yield put(settlementDeleted(result))
    yield put(
      loadCommissionData({
        period: action.payload.period,
        executiveId: action.payload.executiveId
      })
    )
  } catch (error) {
    yield put(
      loadCommissionData({
        period: action.payload.period,
        executiveId: action.payload.executiveId
      })
    )
    yield put(commissionDataFailed(errorMessage(error)))
  }
}

function* loadExecutivesHttp() {
  const { cocap } = yield select(selectCredentials)
  try {
    const executives: ExecutiveOption[] = yield call(getCommissionExecutives, cocap)
    yield put(executivesLoaded(executives))
  } catch (error) {
    yield put(commissionDataFailed(errorMessage(error)))
  }
}

function* loadDataHttp(action: PayloadAction<{ period: string; executiveId?: string }>) {
  const { cocap } = yield select(selectCredentials)
  try {
    const preview: CommissionPreview = yield call(
      getCommissionPreview,
      cocap,
      action.payload.period,
      action.payload.executiveId
    )
    const settlements: CommissionSettlement[] = yield call(
      getCommissionSettlements,
      cocap,
      action.payload.period,
      action.payload.executiveId
    )
    yield put(commissionDataLoaded({ preview, settlements }))
  } catch (error) {
    yield put(commissionDataFailed(errorMessage(error)))
  }
}

function* createSettlementHttp(action: PayloadAction<CreateSettlementPayload>) {
  const { cocap } = yield select(selectCredentials)
  try {
    const settlement: CommissionSettlement = yield call(createCommissionSettlement, cocap, action.payload)
    yield put(settlementCreated(settlement))
    yield put(
      loadCommissionData({
        period: action.payload.period,
        executiveId: action.payload.executiveId
      })
    )
  } catch (error) {
    yield put(commissionDataFailed(errorMessage(error)))
  }
}

export default function* executiveCommissionsSaga() {
  yield takeEvery(loadExecutives.type, loadExecutivesHttp)
  yield takeEvery(loadCommissionData.type, loadDataHttp)
  yield takeLeading(createSettlement.type, createSettlementHttp)
  yield takeLeading(deleteSettlement.type, deleteSettlementHttp)
}
